import { useState, useRef } from 'react';
import { Select, IconButton, Button, Card, Input } from '../ui';
import {
  ChevronDownIcon, ChevronRightIcon, CheckIcon,
  MagnifyingGlassIcon, LightBulbIcon, BeakerIcon, RocketLaunchIcon,
  PlusIcon, TrashIcon, PencilIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  useUpdatePhase, useAddPhase, useDeletePhase, useEngagementTemplates, useApplyTemplate, calcPhaseProgress,
} from '../../hooks/useClients';

const PHASE_ICONS = {
  discovery:      MagnifyingGlassIcon,
  strategy:       LightBulbIcon,
  pilot_planning: BeakerIcon,
  execution:      RocketLaunchIcon,
};

const STATUS = {
  not_started:  {
    label: 'Not Started',
    badgeCls: 'bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted',
    barCls: 'bg-border-light dark:bg-border',
    iconBg: 'bg-background-gray dark:bg-surface-dark',
    iconColor: 'text-text-muted-light dark:text-text-muted',
  },
  in_progress: {
    label: 'In Progress',
    badgeCls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    barCls: 'bg-brand-primary',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  completed: {
    label: 'Completed',
    badgeCls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    barCls: 'bg-green-500',
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
  },
};

const DELIVERABLE_STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'in_review',   label: 'In Review' },
  { value: 'delivered',   label: 'Delivered' },
];

const DELIVERABLE_DOT = {
  pending:     'bg-border dark:bg-border',
  in_progress: 'bg-brand-primary',
  in_review:   'bg-amber-400',
  delivered:   'bg-green-500',
};

function InlineAddRow({ placeholder, onAdd, disabled }) {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  const submit = () => {
    const v = value.trim();
    if (!v) return;
    onAdd(v);
    setValue('');
    inputRef.current?.focus();
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && submit()}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 text-xs border border-border-light dark:border-border rounded-lg px-3 py-2 bg-transparent text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
      />
      <IconButton
        icon={PlusIcon}
        label="Add"
        variant="primary"
        size="sm"
        disabled={disabled || !value.trim()}
        onClick={submit}
        className="flex-shrink-0"
      />
    </div>
  );
}

function PhaseCard({ phase, clientId, isLast, onDeletePhase }) {
  const [open, setOpen] = useState(phase.status === 'in_progress');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(phase.name);
  const [startValue, setStartValue] = useState(phase.startDate || '');
  const [endValue, setEndValue] = useState(phase.endDate || '');
  const updatePhase = useUpdatePhase();

  const startEdit = () => {
    setNameValue(phase.name);
    setStartValue(phase.startDate || '');
    setEndValue(phase.endDate || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setNameValue(phase.name);
    setStartValue(phase.startDate || '');
    setEndValue(phase.endDate || '');
  };

  const saveEdit = () => {
    const n = nameValue.trim();
    const patch = {};
    if (n && n !== phase.name) patch.name = n;
    if (startValue !== (phase.startDate || '')) patch.startDate = startValue || null;
    if (endValue !== (phase.endDate || '')) patch.endDate = endValue || null;
    if (Object.keys(patch).length) {
      updatePhase.mutate({ clientId, phaseId: phase.phaseId, ...patch });
    }
    setEditing(false);
  };

  const s = STATUS[phase.status] || STATUS.not_started;
  const PhaseIcon = PHASE_ICONS[phase.phaseId] || RocketLaunchIcon;

  const completedActivities = phase.activities?.filter(a => a.completed).length ?? 0;
  const totalActivities = phase.activities?.length ?? 0;
  const progress = calcPhaseProgress(phase.activities || [], phase.deliverables || []);
  const deliveredCount = phase.deliverables?.filter(d => d.status === 'delivered').length ?? 0;
  const totalDeliverables = phase.deliverables?.length ?? 0;

  // Handlers

  const handleActivityToggle = (actId) => {
    const updated = phase.activities.map(a =>
      a.id === actId ? { ...a, completed: !a.completed } : a,
    );
    updatePhase.mutate({ clientId, phaseId: phase.phaseId, activities: updated });
  };

  const handleAddActivity = (title) => {
    const updated = [...(phase.activities || []), { id: Date.now().toString(), title, completed: false }];
    updatePhase.mutate({ clientId, phaseId: phase.phaseId, activities: updated });
  };

  const handleDeleteActivity = (actId) => {
    const updated = (phase.activities || []).filter(a => a.id !== actId);
    updatePhase.mutate({ clientId, phaseId: phase.phaseId, activities: updated });
  };

  const handleDeliverableStatus = (delId, status) => {
    const updated = phase.deliverables.map(d => d.id === delId ? { ...d, status } : d);
    updatePhase.mutate({ clientId, phaseId: phase.phaseId, deliverables: updated });
  };

  const handleAddDeliverable = (title) => {
    const updated = [...(phase.deliverables || []), { id: Date.now().toString(), title, status: 'pending' }];
    updatePhase.mutate({ clientId, phaseId: phase.phaseId, deliverables: updated });
  };

  const handleDeleteDeliverable = (delId) => {
    const updated = (phase.deliverables || []).filter(d => d.id !== delId);
    updatePhase.mutate({ clientId, phaseId: phase.phaseId, deliverables: updated });
  };

  return (
    <div className={`relative ${!isLast ? 'pb-3' : ''}`}>
      {/* Vertical connecting line */}
      {!isLast && (
        <div className="absolute left-5 top-[52px] bottom-0 w-px bg-border-light dark:bg-border" />
      )}

      {/* Phase header */}
      <div className="relative z-10 flex items-start gap-4 p-5 rounded-lg border border-border-light dark:border-border bg-white dark:bg-surface-dark">
        {/* Phase icon — clickable to expand */}
        <button
          className={`w-10 h-10 min-w-[40px] rounded-lg flex items-center justify-center ${s.iconBg} hover:opacity-80 transition`}
          onClick={() => setOpen(o => !o)}
        >
          <PhaseIcon className={`w-5 h-5 ${s.iconColor}`} />
        </button>

        <div className="flex-1 min-w-0">
          {/* Top meta row */}
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider">
                Phase {phase.order}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badgeCls}`}>
                {s.label}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {!editing && phase.startDate && (
                <span className="text-xs text-text-muted-light dark:text-text-muted hidden sm:block">
                  {phase.startDate}{phase.endDate ? ` → ${phase.endDate}` : ''}
                </span>
              )}
              <button
                onClick={() => setOpen(o => !o)}
                className="text-text-muted-light dark:text-text-muted hover:text-brand-primary transition"
                aria-label={open ? 'Collapse' : 'Expand'}
              >
                {open
                  ? <ChevronDownIcon className="w-4 h-4" />
                  : <ChevronRightIcon className="w-4 h-4" />
                }
              </button>
            </div>
          </div>

          {/* Phase name */}
          {editing ? (
            <div className="space-y-2 mb-2">
              <Input
                autoFocus
                type="text"
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                placeholder="Phase name"
                className="font-semibold"
              />
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-text-muted-light dark:text-text-muted">Start</label>
                <Input
                  type="date"
                  value={startValue}
                  onChange={e => setStartValue(e.target.value)}
                  className="w-auto"
                />
                <label className="text-xs text-text-muted-light dark:text-text-muted">End</label>
                <Input
                  type="date"
                  value={endValue}
                  onChange={e => setEndValue(e.target.value)}
                  className="w-auto"
                />
              </div>
            </div>
          ) : editingName ? (
            <input
              autoFocus
              type="text"
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onBlur={() => {
                const n = nameValue.trim();
                if (n && n !== phase.name) updatePhase.mutate({ clientId, phaseId: phase.phaseId, name: n });
                setEditingName(false);
              }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setNameValue(phase.name); setEditingName(false); } }}
              className="font-semibold text-text-primary-light dark:text-text-primary mb-2 bg-transparent border-b border-brand-primary focus:outline-none w-full"
            />
          ) : (
            <h3
              className="font-semibold text-text-primary-light dark:text-text-primary mb-2 cursor-text hover:text-brand-primary transition-colors"
              onClick={() => { setNameValue(phase.name); setEditingName(true); }}
            >
              {phase.name}
            </h3>
          )}

          {/* Progress bar */}
          <button
            className="w-full flex items-center gap-3 text-left hover:opacity-75 transition"
            onClick={() => setOpen(o => !o)}
          >
            <div className="flex-1 h-1.5 bg-background-gray dark:bg-background-gray rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${s.barCls}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs font-medium text-text-muted-light dark:text-text-muted w-8 text-right flex-shrink-0">
              {progress}%
            </span>
          </button>

          {/* Counts */}
          <div className="flex gap-4 mt-2">
            {totalActivities > 0 && (
              <span className="text-xs text-text-muted-light dark:text-text-muted">
                {completedActivities}/{totalActivities} activities
              </span>
            )}
            {totalDeliverables > 0 && (
              <span className="text-xs text-text-muted-light dark:text-text-muted">
                {deliveredCount}/{totalDeliverables} delivered
              </span>
            )}
          </div>
        </div>

        {/* Header actions: edit + delete */}
        <div className="flex-shrink-0 flex items-center gap-1">
          {editing ? (
            <>
              <IconButton
                icon={CheckIcon}
                label="Save"
                variant="ghost"
                size="sm"
                onClick={saveEdit}
                className="hover:text-green-600 dark:hover:text-green-400"
              />
              <IconButton
                icon={XMarkIcon}
                label="Cancel"
                variant="ghost"
                size="sm"
                onClick={cancelEdit}
              />
            </>
          ) : confirmDelete ? (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-text-muted-light dark:text-text-muted">Delete?</span>
              <Button variant="danger" size="sm" onClick={() => onDeletePhase(phase.phaseId)}>Yes</Button>
              <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)}>No</Button>
            </div>
          ) : (
            <>
              <IconButton
                icon={PencilIcon}
                label="Edit phase"
                variant="ghost"
                size="sm"
                onClick={startEdit}
                className="hover:text-brand-primary"
              />
              <IconButton
                icon={TrashIcon}
                label="Delete phase"
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                className="hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              />
            </>
          )}
        </div>
      </div>

      {/* Expanded detail panel */}
      {open && (
        <div className="ml-14 mt-2 rounded-lg border border-border-light dark:border-border overflow-hidden bg-white dark:bg-surface-dark">
          {/* Activities + Deliverables */}
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border-light dark:divide-border">
            {/* Activities */}
            <div className="p-4">
              <h4 className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${s.barCls}`} />
                Activities
              </h4>
              <ul className="space-y-1.5">
                {phase.activities?.map(a => (
                  <li
                    key={a.id}
                    className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-background-light dark:bg-background border border-border-light dark:border-border group"
                  >
                    <button
                      className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                        a.completed
                          ? 'bg-green-500 border-green-500'
                          : 'border-border-light dark:border-border group-hover:border-green-400 dark:group-hover:border-green-500'
                      }`}
                      onClick={() => handleActivityToggle(a.id)}
                    >
                      {a.completed && <CheckIcon className="w-2.5 h-2.5 text-white" />}
                    </button>
                    <span
                      className={`flex-1 text-xs leading-relaxed select-none cursor-pointer ${
                        a.completed
                          ? 'line-through text-text-muted-light dark:text-text-muted'
                          : 'text-text-primary-light dark:text-text-primary'
                      }`}
                      onClick={() => handleActivityToggle(a.id)}
                    >
                      {a.title}
                    </span>
                    <IconButton
                      icon={TrashIcon}
                      label="Delete activity"
                      variant="ghost"
                      size="xs"
                      onClick={() => handleDeleteActivity(a.id)}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 hover:text-red-500 dark:hover:text-red-400"
                    />
                  </li>
                ))}
              </ul>
              <InlineAddRow
                placeholder="Add activity…"
                onAdd={handleAddActivity}
              />
            </div>

            {/* Deliverables */}
            <div className="p-4">
              <h4 className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-primary" />
                Deliverables
              </h4>
              <ul className="space-y-1.5">
                {phase.deliverables?.map(d => (
                  <li key={d.id} className="px-3 py-2.5 rounded-lg bg-background-light dark:bg-background border border-border-light dark:border-border group">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DELIVERABLE_DOT[d.status] || DELIVERABLE_DOT.pending}`} />
                      <span className="text-xs text-text-primary-light dark:text-text-primary flex-1 leading-relaxed">{d.title}</span>
                      <IconButton
                        icon={TrashIcon}
                        label="Delete deliverable"
                        variant="ghost"
                        size="xs"
                        onClick={() => handleDeleteDeliverable(d.id)}
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 hover:text-red-500 dark:hover:text-red-400"
                      />
                    </div>
                    <Select
                      value={d.status}
                      onChange={e => handleDeliverableStatus(d.id, e.target.value)}
                      options={DELIVERABLE_STATUS_OPTIONS}
                    />
                  </li>
                ))}
              </ul>
              <InlineAddRow
                placeholder="Add deliverable…"
                onAdd={handleAddDeliverable}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddPhaseForm({ clientId, onDone }) {
  const [name, setName] = useState('');
  const addPhase = useAddPhase();

  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    await addPhase.mutateAsync({ clientId, name: n });
    setName('');
    onDone();
  };

  return (
    <div className="flex items-center gap-2 mt-3 p-4 rounded-lg border border-dashed border-border-light dark:border-border bg-white dark:bg-surface-dark">
      <input
        autoFocus
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onDone(); }}
        placeholder="Phase name…"
        className="flex-1 text-sm border border-border-light dark:border-border rounded-lg px-3 py-2 bg-transparent text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary"
      />
      <Button
        variant="primary"
        size="sm"
        onClick={submit}
        disabled={!name.trim() || addPhase.isPending}
      >
        {addPhase.isPending ? 'Adding…' : 'Add'}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={onDone}
      >
        Cancel
      </Button>
    </div>
  );
}

export default function EngagementPhases({ phases, clientId }) {
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [startEmpty, setStartEmpty] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const deletePhase = useDeletePhase();
  const { data: templates = [] } = useEngagementTemplates();
  const applyTemplate = useApplyTemplate();

  if (!phases?.length && !startEmpty) {
    const templateOptions = [
      { value: '', label: 'Select a template…' },
      ...templates.map(t => ({ value: t.id, label: t.name })),
    ];
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-6 max-w-md mx-auto">
        <div className="w-12 h-12 rounded-full bg-background-gray dark:bg-surface-dark flex items-center justify-center">
          <RocketLaunchIcon className="w-6 h-6 text-text-muted-light dark:text-text-muted" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-text-primary-light dark:text-text-primary mb-1">
            No engagement phases yet
          </p>
          <p className="text-xs text-text-muted-light dark:text-text-muted">
            Start from a template or build phases from scratch.
          </p>
        </div>

        <Card padding="md" className="w-full">
          <p className="text-xs font-medium uppercase tracking-wider text-text-muted mb-3">Start from template</p>
          <div className="flex gap-2">
            <div className="flex-1">
              <Select
                value={selectedTemplate}
                onChange={e => setSelectedTemplate(e.target.value)}
                options={templateOptions}
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              disabled={!selectedTemplate || applyTemplate.isPending}
              onClick={() => applyTemplate.mutate({ clientId, templateId: selectedTemplate })}
            >
              {applyTemplate.isPending ? 'Applying…' : 'Apply'}
            </Button>
          </div>
          {templates.length === 0 && (
            <p className="text-xs text-text-muted mt-2">
              No templates available — create one under Clients → Templates.
            </p>
          )}
        </Card>

        <div className="flex items-center gap-3 w-full">
          <div className="flex-1 h-px bg-border-light dark:bg-border" />
          <span className="text-xs text-text-muted">or</span>
          <div className="flex-1 h-px bg-border-light dark:bg-border" />
        </div>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => { setStartEmpty(true); setShowAddPhase(true); }}
        >
          Start with empty phases
        </Button>
      </div>
    );
  }

  const allActivities = phases.flatMap(p => p.activities || []);
  const allDeliverables = phases.flatMap(p => p.deliverables || []);
  const completedActivities = allActivities.filter(a => a.completed).length;
  const deliveredCount = allDeliverables.filter(d => d.status === 'delivered').length;
  const overallProgress = calcPhaseProgress(allActivities, allDeliverables);
  const completedPhases = phases.filter(p => p.status === 'completed').length;

  return (
    <div className="space-y-6">
      {/* KPI summary row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Overall Progress', value: `${overallProgress}%` },
          { label: 'Activities Done',  value: `${completedActivities}/${allActivities.length}` },
          { label: 'Deliverables',     value: `${deliveredCount}/${allDeliverables.length}` },
        ].map(kpi => (
          <Card key={kpi.label} padding="sm">
            <p className="text-xl font-bold text-text-primary-light dark:text-text-primary">{kpi.value}</p>
            <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mt-0.5">{kpi.label}</p>
          </Card>
        ))}
      </div>

      {/* Phase timeline */}
      <div>
        <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-4">
          {completedPhases}/{phases.length} phases complete
        </p>
        <div>
          {phases.map((phase, idx) => (
            <PhaseCard
              key={phase.phaseId}
              phase={phase}
              clientId={clientId}
              isLast={idx === phases.length - 1 && !showAddPhase}
              onDeletePhase={(phaseId) => deletePhase.mutate({ clientId, phaseId })}
            />
          ))}
        </div>

        {showAddPhase ? (
          <AddPhaseForm clientId={clientId} onDone={() => setShowAddPhase(false)} />
        ) : (
          <button
            onClick={() => setShowAddPhase(true)}
            className="mt-3 flex items-center gap-2 text-xs text-text-muted-light dark:text-text-muted hover:text-brand-primary dark:hover:text-brand-primary transition"
          >
            <div className="w-10 h-10 rounded-xl border-2 border-dashed border-border-light dark:border-border flex items-center justify-center">
              <PlusIcon className="w-4 h-4" />
            </div>
            <span className="font-medium">Add phase</span>
          </button>
        )}
      </div>
    </div>
  );
}
